import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SectionHeading, EmptySection, Field, RiskBadge, BoolBadge, TagList } from './shared';

interface Props {
  forecastedActivities: any[];
  outstandingItems: any[];
  summary: any | null;
}

const PRIORITY_COLORS: Record<string, string> = {
  high: 'bg-mozaic-red/15 text-mozaic-red',
  medium: 'bg-mozaic-orange/15 text-mozaic-orange',
  low: 'bg-mozaic-green/15 text-mozaic-green',
};

export default function ForecastingSection({ forecastedActivities, outstandingItems, summary }: Props) {
  const hasData = forecastedActivities.length > 0 || outstandingItems.length > 0 || summary;

  return (
    <section>
      <SectionHeading
        id="section-forecasting"
        title="Activity Forecasting"
        count={forecastedActivities.length}
      />
      {!hasData ? (
        <EmptySection name="forecasting data" />
      ) : (
        <div className="mt-2 space-y-4">
          {/* Forecasted activities */}
          {forecastedActivities.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">Forecasted Activities</h3>
              {forecastedActivities.map((row: any) => (
                <Card key={row.id}>
                  <CardContent className="pt-4">
                    <div className="mb-2 flex items-center gap-2">
                      <RiskBadge level={row.anticipated_risk} />
                      <BoolBadge value={row.is_permits_required} />
                      {row.is_permits_required && (
                        <span className="text-xs text-muted-foreground">Permits required</span>
                      )}
                    </div>
                    <p className="mb-3 text-sm font-medium">{row.description}</p>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      <Field
                        label="Estimated Duration"
                        value={row.estimated_duration != null ? `${row.estimated_duration} min` : null}
                      />
                      <div className="col-span-full">
                        <Field label="Activity Tags" value={<TagList tags={row.activity_tags} />} />
                      </div>
                      {row.risk_notes && (
                        <div className="col-span-full">
                          <Field label="Risk Notes" value={row.risk_notes} />
                        </div>
                      )}
                      {row.notes && (
                        <div className="col-span-full">
                          <Field label="Notes" value={row.notes} />
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Outstanding items */}
          {outstandingItems.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">
                Outstanding Items ({outstandingItems.length})
              </h3>
              {outstandingItems.map((row: any) => (
                <Card key={row.id}>
                  <CardContent className="flex items-center gap-3 pt-4">
                    <Badge className={PRIORITY_COLORS[row.priority] ?? 'bg-muted text-muted-foreground'}>
                      {row.priority}
                    </Badge>
                    <span className="text-sm">{row.description}</span>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Summary */}
          {summary && (
            <Card className="border-mozaic-navy/20 bg-mozaic-navy/5">
              <CardContent className="pt-4">
                <h3 className="mb-3 text-sm font-medium">Forecast Summary</h3>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Field
                    label="Risk Assessment"
                    value={
                      <Badge variant="outline">
                        {(summary.overall_risk_assessment ?? '').replace(/_/g, ' ')}
                      </Badge>
                    }
                  />
                  <Field
                    label="Resource Adequacy"
                    value={
                      <Badge variant="outline">
                        {(summary.resource_adequacy ?? '').replace(/_/g, ' ')}
                      </Badge>
                    }
                  />
                  {summary.risk_comments && (
                    <div className="col-span-full">
                      <Field label="Risk Comments" value={summary.risk_comments} />
                    </div>
                  )}
                  {summary.resource_comments && (
                    <div className="col-span-full">
                      <Field label="Resource Comments" value={summary.resource_comments} />
                    </div>
                  )}
                  {summary.next_shift_narrative && (
                    <div className="col-span-full">
                      <Field label="Next Shift Narrative" value={summary.next_shift_narrative} />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </section>
  );
}
